/** Phase 9 Slice 4 — /app/settings/legal — Imprint + Privacy + Consent editor.
 *
 *  Owner/admin-only (gated by tenants.write). Lets operators populate the
 *  five required DSGVO/Impressum fields that the campaign-publish gate
 *  enforces, plus the optional DPO contact.
 *
 *  Big-form layout: two Markdown <textarea>s (Imprint, Privacy Policy)
 *  side-by-side with a DE/EN tab toggle, then the consent text + data
 *  controller fields, then a "Vorschau" pane that renders the active tab.
 *  Save calls PUT /legal-documents (audit-logged), success bumps `version`.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../lib/api";
import { useLocale } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle }
  from "../../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent }
  from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

const COPY = {
  de: {
    title: "Rechtsdokumente",
    subtitle: "Impressum, Datenschutz und Einwilligungstext für deinen Tenant.",
    warn_h: "Pflichtfelder fehlen",
    warn_body: "Kampagnen können nicht veröffentlicht werden, bis diese Felder ausgefüllt sind:",
    ok_h: "Konfiguration vollständig",
    imprint: "Impressum",
    privacy: "Datenschutzerklärung",
    consent: "Einwilligungstext für Lead-Formulare",
    consent_help: "Max. 500 Zeichen.",
    dc_h: "Verantwortlicher (DSGVO Art. 13)",
    dc_name: "Name", dc_address: "Adresse",
    dc_email: "E-Mail", dc_phone: "Telefon",
    dpo_h: "Datenschutzbeauftragter (optional)",
    dpo_name: "Name", dpo_email: "E-Mail", dpo_phone: "Telefon",
    save: "Speichern",
    saving: "Speichere…",
    saved: "Gespeichert (Version {v})",
    err: "Fehler beim Speichern.",
    preview: "Vorschau",
    version: "Version",
  },
  en: {
    title: "Legal Documents",
    subtitle: "Imprint, privacy policy and consent text for your tenant.",
    warn_h: "Required fields missing",
    warn_body: "Campaigns cannot be published until these fields are populated:",
    ok_h: "Configuration complete",
    imprint: "Imprint",
    privacy: "Privacy Policy",
    consent: "Consent text for lead forms",
    consent_help: "Max 500 characters.",
    dc_h: "Data Controller (GDPR Art. 13)",
    dc_name: "Name", dc_address: "Address",
    dc_email: "Email", dc_phone: "Phone",
    dpo_h: "Data Protection Officer (optional)",
    dpo_name: "Name", dpo_email: "Email", dpo_phone: "Phone",
    save: "Save",
    saving: "Saving…",
    saved: "Saved (version {v})",
    err: "Save failed.",
    preview: "Preview",
    version: "Version",
  },
};

const t = (l, k, v) => {
  const s = (COPY[l] || COPY.de)[k] || k;
  return v ? s.replace(/\{(\w+)\}/g, (_, x) =>
    v[x] !== undefined ? String(v[x]) : `{${x}}`) : s;
};

// Phase 9.4.2 — `?#field=<key>` deep-link → smooth-scroll + focus + pulse.
// Hash format: `#field=imprint_de,privacy_policy_de` (CSV).
const HASH_FIELD_TO_TESTID = {
  imprint_de: "legal-imprint-de",
  imprint_en: "legal-imprint-en",
  privacy_policy_de: "legal-privacy-de",
  privacy_policy_en: "legal-privacy-en",
  consent_text_lead_form_de: "legal-consent-de",
  consent_text_lead_form_en: "legal-consent-en",
  "data_controller.name": "legal-dc-name",
  "data_controller.address": "legal-dc-address",
  "data_controller.email": "legal-dc-email",
  "data_controller.phone": "legal-dc-phone",
};

function parseFieldHash(hash) {
  if (!hash) return [];
  const m = hash.match(/^#field=(.+)$/);
  if (!m) return [];
  return decodeURIComponent(m[1]).split(",").map((s) => s.trim()).filter(Boolean);
}

export default function SettingsLegal() {
  const { locale } = useLocale();
  const [doc, setDoc] = useState(null);
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("de");
  const [saving, setSaving] = useState(false);
  const [pulseTestid, setPulseTestid] = useState(null);   // Phase 9.4.2
  const location = useLocation();
  // Phase 9.4.2.3 — mirror BOTH react-router state AND the native
  // `hashchange` event into local state. React Router v6 only listens
  // to `popstate`, which is NOT fired by `history.pushState(...)`;
  // SPA-nav patterns that dispatch a manual `HashChangeEvent` (the
  // way the toast CTA + any external hash-changer drives this page)
  // would otherwise leave `useLocation().hash` stale.
  const [currentHash, setCurrentHash] = useState(
    typeof window !== "undefined" ? window.location.hash : "");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onHashChange = () => setCurrentHash(window.location.hash);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  useEffect(() => { setCurrentHash(location.hash); }, [location.hash]);
  // Phase 9.4.2.2 — track the last hash we acted on so SPA-navigating
  // between two `/app/settings/legal#field=…` URLs (same path, only
  // hash differs) re-triggers the deep-link effect. The previous
  // boolean ref locked out subsequent hashes.
  const lastAppliedHash = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/legal-documents");
        setDoc(r.data);
        const s = await api.get("/legal-documents/status");
        setStatus(s.data);
      } catch (_) {}
    })();
  }, []);

  // Phase 9.4.2 — react to `#field=<key>` hash deep-links from the
  // compliance error toast. Runs once after the form mounts (`doc` is
  // populated). Switches DE/EN tab if the targeted field lives in the
  // other locale, scrolls the element into view, focuses it, and adds
  // a 1.5s soft highlight pulse.
  // Phase 9.4.2.2 — hardened: pulse-clear lives in a dedicated effect
  // so it survives StrictMode double-mount and effect re-runs (was
  // racing the initial setTimeout cleanup on slow loads).
  useEffect(() => {
    if (!doc) return;
    if (lastAppliedHash.current === currentHash) return;
    const fields = parseFieldHash(currentHash);
    if (fields.length === 0) {
      lastAppliedHash.current = currentHash;
      return;
    }
    const first = fields[0];
    const testid = HASH_FIELD_TO_TESTID[first];
    if (!testid) {
      lastAppliedHash.current = currentHash;
      return;
    }
    // Switch tab if the targeted field is locale-specific (textareas
    // live inside <TabsContent>; non-active tab is hidden but mounted).
    if (first.endsWith("_de") && tab !== "de") setTab("de");
    if (first.endsWith("_en") && tab !== "en") setTab("en");
    // Defer focus until React commits the textareas. Two rAFs is the
    // safe pattern for "after next paint" in React 18; falls back to
    // setTimeout when rAF is unavailable (jsdom).
    let cancelled = false;
    const apply = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (el) {
        try { el.scrollIntoView({ behavior: "smooth", block: "center" }); }
        catch (_) { /* jsdom */ }
        try { el.focus({ preventScroll: true }); } catch (_) {}
      }
      setPulseTestid(testid);
      lastAppliedHash.current = currentHash;
    };
    const raf = typeof requestAnimationFrame === "function"
      ? requestAnimationFrame(() => requestAnimationFrame(apply))
      : null;
    const fallback = setTimeout(apply, 50);
    return () => {
      cancelled = true;
      if (raf !== null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(raf);
      }
      clearTimeout(fallback);
    };
  }, [doc, currentHash, tab]);

  // Phase 9.4.2.2 — pulse auto-clear. Lives in its own effect so it
  // can't be cancelled by an unrelated re-run of the deep-link effect.
  useEffect(() => {
    if (!pulseTestid) return;
    const handle = setTimeout(() => setPulseTestid(null), 1500);
    return () => clearTimeout(handle);
  }, [pulseTestid]);

  function set(k, v) { setDoc({ ...doc, [k]: v }); }
  function setDc(k, v) {
    setDoc({ ...doc, data_controller: {
      ...(doc.data_controller || {}), [k]: v } });
  }
  function setDpo(k, v) {
    setDoc({ ...doc, dpo_contact: {
      ...(doc.dpo_contact || {}), [k]: v } });
  }

  async function save() {
    setSaving(true);
    try {
      const r = await api.put("/legal-documents", doc);
      setDoc(r.data);
      toast.success(t(locale, "saved", { v: r.data.version }));
      const s = await api.get("/legal-documents/status");
      setStatus(s.data);
    } catch (_) {
      toast.error(t(locale, "err"));
    } finally { setSaving(false); }
  }

  if (!doc) return (
    <div className="p-6 text-sm text-muted-foreground"
          data-testid="legal-settings-loading">Loading…</div>
  );

  const missing = status?.missing_fields || [];
  const configured = status?.configured === true;

  return (
    <div className="p-6 space-y-6" data-testid="settings-legal-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t(locale, "title")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t(locale, "subtitle")}{" "}
          <Badge variant="outline" className="ml-2 font-mono text-[10px]">
            {t(locale, "version")} {doc.version || 1}
          </Badge>
        </p>
      </div>

      {missing.length > 0 ? (
        <div className="rounded-md border border-amber-300 bg-amber-50
                          dark:bg-amber-950/30 dark:border-amber-800 p-3"
              data-testid="legal-missing-banner">
          <div className="flex items-center gap-2 text-sm font-medium
                            text-amber-900 dark:text-amber-200">
            <AlertTriangle className="size-4" /> {t(locale, "warn_h")}
          </div>
          <p className="text-xs text-amber-900 dark:text-amber-100 mt-1">
            {t(locale, "warn_body")}
          </p>
          <ul className="text-xs font-mono text-amber-950 dark:text-amber-100 mt-1
                          list-disc list-inside">
            {missing.map((m) => <li key={m}>{m}</li>)}
          </ul>
        </div>
      ) : (
        <div className="rounded-md border border-emerald-300 bg-emerald-50
                          dark:bg-emerald-950/30 dark:border-emerald-800 p-3"
              data-testid="legal-ok-banner">
          <div className="flex items-center gap-2 text-sm font-medium
                            text-emerald-900 dark:text-emerald-200">
            <CheckCircle2 className="size-4" /> {t(locale, "ok_h")}
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="de" data-testid="legal-tab-de">DE</TabsTrigger>
          <TabsTrigger value="en" data-testid="legal-tab-en">EN</TabsTrigger>
        </TabsList>

        {["de", "en"].map((lng) => (
          <TabsContent value={lng} key={lng}
                          className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">{t(locale, "imprint")} ({lng.toUpperCase()})</Label>
                <textarea
                  data-testid={`legal-imprint-${lng}`}
                  className={`w-full h-48 text-xs font-mono p-2 rounded-md border bg-background transition ${pulseTestid === `legal-imprint-${lng}` ? "ring-2 ring-amber-400 animate-pulse" : ""}`}
                  value={doc[`imprint_${lng}`] || ""}
                  onChange={(e) => set(`imprint_${lng}`, e.target.value)}/>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t(locale, "privacy")} ({lng.toUpperCase()})</Label>
                <textarea
                  data-testid={`legal-privacy-${lng}`}
                  className={`w-full h-48 text-xs font-mono p-2 rounded-md border bg-background transition ${pulseTestid === `legal-privacy-${lng}` ? "ring-2 ring-amber-400 animate-pulse" : ""}`}
                  value={doc[`privacy_policy_${lng}`] || ""}
                  onChange={(e) => set(`privacy_policy_${lng}`, e.target.value)}/>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">
                  {t(locale, "consent")} ({lng.toUpperCase()})
                </Label>
                <textarea
                  data-testid={`legal-consent-${lng}`}
                  maxLength={500}
                  className={`w-full h-20 text-xs p-2 rounded-md border bg-background transition ${pulseTestid === `legal-consent-${lng}` ? "ring-2 ring-amber-400 animate-pulse" : ""}`}
                  value={doc[`consent_text_lead_form_${lng}`] || ""}
                  onChange={(e) => set(`consent_text_lead_form_${lng}`,
                                              e.target.value)}/>
                <p className="text-[10px] text-muted-foreground">
                  {t(locale, "consent_help")}
                </p>
              </div>
            </div>

            <div className="rounded-md border bg-muted/30 p-3"
                  data-testid={`legal-preview-${lng}`}>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                {t(locale, "preview")}
              </div>
              <div className="text-xs whitespace-pre-line">
                <b>{t(locale, "imprint")}</b>
                <br/>
                {doc[`imprint_${lng}`] || "—"}
                <hr className="my-3 border-border" />
                <b>{t(locale, "privacy")}</b>
                <br/>
                {(doc[`privacy_policy_${lng}`] || "—").slice(0, 600)}
                {(doc[`privacy_policy_${lng}`] || "").length > 600 && "…"}
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t(locale, "dc_h")}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{t(locale, "dc_name")}</Label>
            <Input data-testid="legal-dc-name"
                    className={pulseTestid === "legal-dc-name" ? "ring-2 ring-amber-400 animate-pulse" : ""}
                    value={doc.data_controller?.name || ""}
                    onChange={(e) => setDc("name", e.target.value)}/>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t(locale, "dc_address")}</Label>
            <Input data-testid="legal-dc-address"
                    className={pulseTestid === "legal-dc-address" ? "ring-2 ring-amber-400 animate-pulse" : ""}
                    value={doc.data_controller?.address || ""}
                    onChange={(e) => setDc("address", e.target.value)}/>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t(locale, "dc_email")}</Label>
            <Input data-testid="legal-dc-email"
                    className={pulseTestid === "legal-dc-email" ? "ring-2 ring-amber-400 animate-pulse" : ""}
                    value={doc.data_controller?.email || ""}
                    onChange={(e) => setDc("email", e.target.value)}/>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t(locale, "dc_phone")}</Label>
            <Input data-testid="legal-dc-phone"
                    className={pulseTestid === "legal-dc-phone" ? "ring-2 ring-amber-400 animate-pulse" : ""}
                    value={doc.data_controller?.phone || ""}
                    onChange={(e) => setDc("phone", e.target.value)}/>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t(locale, "dpo_h")}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input data-testid="legal-dpo-name"
                  placeholder={t(locale, "dpo_name")}
                  value={doc.dpo_contact?.name || ""}
                  onChange={(e) => setDpo("name", e.target.value)}/>
          <Input data-testid="legal-dpo-email"
                  placeholder={t(locale, "dpo_email")}
                  value={doc.dpo_contact?.email || ""}
                  onChange={(e) => setDpo("email", e.target.value)}/>
          <Input data-testid="legal-dpo-phone"
                  placeholder={t(locale, "dpo_phone")}
                  value={doc.dpo_contact?.phone || ""}
                  onChange={(e) => setDpo("phone", e.target.value)}/>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button data-testid="legal-save-btn"
                disabled={saving} onClick={save}>
          {saving ? t(locale, "saving") : t(locale, "save")}
        </Button>
      </div>
    </div>
  );
}
